import { ref, onMounted, onUnmounted } from 'vue'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function usePWAInstall() {
  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
  const showInstallPrompt = ref(false)
  const isInstalled = ref(false)
  const isIOS = ref(false)
  const isSafari = ref(false)
  const isMacSafari = ref(false)
  const showIOSInstructions = ref(false)

  const checkIsIOS = (): boolean => {
    if (typeof window === 'undefined' || !window.navigator) return false
    const ua = window.navigator.userAgent.toLowerCase()
    const isIOSDevice = /iphone|ipad|ipod/.test(ua)
    // iPadOS 13+ desktop-class browsing reports as MacIntel with touch points
    const isIPadOS = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1
    return isIOSDevice || isIPadOS
  }

  const checkIsSafari = (): boolean => {
    if (typeof window === 'undefined' || !window.navigator) return false
    const ua = window.navigator.userAgent.toLowerCase()
    return /safari/.test(ua) && !/chrome|crios|crmo|android|edg|opr/.test(ua)
  }

  const isStandalone = (): boolean => {
    if (typeof window === 'undefined') return false
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    )
  }

  const checkIfDismissedRecently = (): boolean => {
    if (typeof window === 'undefined') return false
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10)
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24)
      return daysSinceDismissed < 7
    }
    return false
  }

  const handleBeforeInstallPrompt = (e: Event) => {
    e.preventDefault()
    deferredPrompt.value = e as BeforeInstallPromptEvent
    showInstallPrompt.value = true
  }

  const handleAppInstalled = () => {
    deferredPrompt.value = null
    showInstallPrompt.value = false
    showIOSInstructions.value = false
    isInstalled.value = true
  }

  const installPWA = async () => {
    // 1. For Chromium-based browsers that support beforeinstallprompt
    if (deferredPrompt.value) {
      deferredPrompt.value.prompt()
      const { outcome } = await deferredPrompt.value.userChoice

      if (outcome === 'accepted') {
        showInstallPrompt.value = false
      }
      deferredPrompt.value = null
      return
    }

    // 2. For iOS Safari or macOS Safari where programmatic prompt is not supported by Apple
    if (isIOS.value || isSafari.value) {
      showIOSInstructions.value = true
      return
    }
  }

  const closeIOSInstructions = () => {
    showIOSInstructions.value = false
  }

  const dismissInstallPrompt = () => {
    showInstallPrompt.value = false
    showIOSInstructions.value = false
    localStorage.setItem('pwa-install-dismissed', Date.now().toString())
  }

  onMounted(() => {
    if (isStandalone()) {
      isInstalled.value = true
      return
    }

    if (checkIfDismissedRecently()) {
      return
    }

    const iosDetected = checkIsIOS()
    const safariDetected = checkIsSafari()

    isIOS.value = iosDetected
    isSafari.value = safariDetected
    isMacSafari.value = safariDetected && !iosDetected

    // Safari (both iOS and macOS) never fires 'beforeinstallprompt'.
    // If user is on iOS or Safari and hasn't installed or dismissed yet, show the install banner.
    if (iosDetected || safariDetected) {
      showInstallPrompt.value = true
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
  })

  onUnmounted(() => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', handleAppInstalled)
  })

  return {
    showInstallPrompt,
    isInstalled,
    isIOS,
    isSafari,
    isMacSafari,
    showIOSInstructions,
    installPWA,
    closeIOSInstructions,
    dismissInstallPrompt
  }
}
